import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';

export function useAwardNotifications() {
  const [hasUnclaimed, setHasUnclaimed] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [hasCheckedOnMount, setHasCheckedOnMount] = useState(false);
  const [hasBeenDismissed, setHasBeenDismissed] = useState(false);
  const [lastAwardCount, setLastAwardCount] = useState(0);
  const { toast } = useToast();
  
  // Query for pending awards
  const { 
    data, 
    isLoading, 
    error, 
    refetch 
  } = useQuery({
    queryKey: ['player-awards-pending'],
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/awards/player/awards/pending');
      const result = await response.json();
      return result.pendingAwards ?? [];
    },
    enabled: false, // Don't auto-fetch on mount
    staleTime: 0, // Always consider data stale
    gcTime: 0, // Don't cache the data
  });
  
  const pendingAwards = data ?? [];
  
  // Check for pending awards on mount
  useEffect(() => {
    const checkForAwards = async () => {
      try {
        await refetch();
        setHasCheckedOnMount(true);
      } catch (err) {
        console.error('Error fetching awards:', err);
      }
    };
    
    checkForAwards();
  }, [refetch]);
  
  // Update hasUnclaimed state when data changes
  useEffect(() => {
    const currentAwardCount = pendingAwards?.length ?? 0;
    
    if (currentAwardCount > 0) {
      setHasUnclaimed(true);
      
      // Only auto-open dialog if:
      // 1. We've done our initial check
      // 2. Dialog is not already open
      // 3. User hasn't manually dismissed it OR we have new awards (count increased)
      const hasNewAwards = currentAwardCount > lastAwardCount;
      
      if (hasCheckedOnMount && !isDialogOpen && (!hasBeenDismissed || hasNewAwards)) {
        setIsDialogOpen(true);
        // Reset dismissal state when showing new awards
        if (hasNewAwards) {
          setHasBeenDismissed(false);
        }
      }
    } else {
      setHasUnclaimed(false);
      // Reset dismissal state when no awards
      setHasBeenDismissed(false);
    }
    
    // Update last award count
    setLastAwardCount(currentAwardCount);
  }, [pendingAwards, hasCheckedOnMount, isDialogOpen, hasBeenDismissed, lastAwardCount]);
  
  // Handle claiming all awards
  const handleClaimAll = async () => {
    setIsDialogOpen(false);
    setHasUnclaimed(false);
    // Reset dismissal state when awards are claimed (not dismissed)
    setHasBeenDismissed(false);
    setLastAwardCount(0); // Reset count since awards were claimed
    
    // Refetch awards to ensure we have the latest state
    await refetch();
    
    // Show a toast confirming awards claimed
    if (pendingAwards.length > 0) {
      toast({
        title: "Awards Claimed!",
        description: `You've successfully claimed ${pendingAwards.length} award(s).`,
        duration: 5000,
      });
    }
  };

  // Handle manual dialog dismissal (X button or ESC)
  const handleDialogDismiss = (open: boolean) => {
    setIsDialogOpen(open);
    if (!open) {
      // Mark as dismissed when user manually closes the dialog
      setHasBeenDismissed(true);
    }
  };
  
  return {
    pendingAwards,
    hasUnclaimed,
    isLoading,
    error,
    isDialogOpen,
    setIsDialogOpen: handleDialogDismiss, // Use our custom handler instead
    handleClaimAll,
    refetch
  };
}
